import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";
import { triplePSDatasource } from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { ServiceTimePerStop, DumpWeightByMaterial } from "../../models";
import { logout } from "../Auth/authSlice";

export interface SettingsState {
  getServiceTime: APIResult<ServiceTimePerStop>;
  updateServiceTime: APIResult<ServiceTimePerStop>;
  serviceTime?: ServiceTimePerStop;
  getDumpWeight: APIResult<DumpWeightByMaterial>;
  updateDumpWeight: APIResult<DumpWeightByMaterial>;
  dumpWeight?: DumpWeightByMaterial;
  getDailyWorkTimeCap: APIResult<number>;
  updateDailyWorkTimeCap: APIResult<number>;
  dailyWorkTimeCap?: number;
  getDeliveryPhotoRequired: APIResult<boolean>;
  updateDeliveryPhotoRequired: APIResult<boolean>;
  deliveryPhotoRequired?: boolean;
  getDumpReceiptRequired: APIResult<boolean>;
  updateDumpReceiptRequired: APIResult<boolean>;
  dumpReceiptRequired?: boolean;
  getPickupPhotoRequired: APIResult<boolean>;
  updatePickupPhotoRequired: APIResult<boolean>;
  pickupPhotoRequired?: boolean;
}

const initialState: SettingsState = {
  getServiceTime: emptyAPIResult(),
  updateServiceTime: emptyAPIResult(),
  getDumpWeight: emptyAPIResult(),
  updateDumpWeight: emptyAPIResult(),
  getDailyWorkTimeCap: emptyAPIResult(),
  updateDailyWorkTimeCap: emptyAPIResult(),
  getDeliveryPhotoRequired: emptyAPIResult(),
  updateDeliveryPhotoRequired: emptyAPIResult(),
  getDumpReceiptRequired: emptyAPIResult(),
  updateDumpReceiptRequired: emptyAPIResult(),
  getPickupPhotoRequired: emptyAPIResult(),
  updatePickupPhotoRequired: emptyAPIResult(),
};

export const getServiceTime = createAsyncThunk(
  "settings/getServiceTime",
  async () => {
    return triplePSDatasource.getServiceTime();
  }
);

export const updateServiceTime = createAsyncThunk(
  "settings/updateServiceTime",
  async (value: ServiceTimePerStop, thunkAPI) => {
    const updated = await triplePSDatasource.updateServiceTime(value);
    // Refetch so the section reflects server truth after the upsert.
    await thunkAPI.dispatch(getServiceTime());
    return updated;
  }
);

export const getDumpWeight = createAsyncThunk(
  "settings/getDumpWeight",
  async () => {
    return triplePSDatasource.getDumpWeightByMaterial();
  }
);

export const updateDumpWeight = createAsyncThunk(
  "settings/updateDumpWeight",
  async (value: DumpWeightByMaterial, thunkAPI) => {
    const updated = await triplePSDatasource.updateDumpWeightByMaterial(value);
    // Refetch so the section reflects server truth after the upsert.
    await thunkAPI.dispatch(getDumpWeight());
    return updated;
  }
);

export const getDailyWorkTimeCap = createAsyncThunk(
  "settings/getDailyWorkTimeCap",
  async () => {
    return triplePSDatasource.getDailyWorkTimeCap();
  }
);

export const updateDailyWorkTimeCap = createAsyncThunk(
  "settings/updateDailyWorkTimeCap",
  async (value: number, thunkAPI) => {
    const updated = await triplePSDatasource.updateDailyWorkTimeCap(value);
    // Refetch so the section reflects server truth after the upsert.
    await thunkAPI.dispatch(getDailyWorkTimeCap());
    return updated;
  }
);

export const getDeliveryPhotoRequired = createAsyncThunk(
  "settings/getDeliveryPhotoRequired",
  async () => {
    return triplePSDatasource.getDeliveryPhotoRequired();
  }
);

export const updateDeliveryPhotoRequired = createAsyncThunk(
  "settings/updateDeliveryPhotoRequired",
  async (value: boolean, thunkAPI) => {
    const updated = await triplePSDatasource.updateDeliveryPhotoRequired(value);
    // Refetch so the section reflects server truth after the upsert.
    await thunkAPI.dispatch(getDeliveryPhotoRequired());
    return updated;
  }
);

export const getDumpReceiptRequired = createAsyncThunk(
  "settings/getDumpReceiptRequired",
  async () => {
    return triplePSDatasource.getDumpReceiptRequired();
  }
);

export const updateDumpReceiptRequired = createAsyncThunk(
  "settings/updateDumpReceiptRequired",
  async (value: boolean, thunkAPI) => {
    const updated = await triplePSDatasource.updateDumpReceiptRequired(value);
    // Refetch so the section reflects server truth after the upsert.
    await thunkAPI.dispatch(getDumpReceiptRequired());
    return updated;
  }
);

export const getPickupPhotoRequired = createAsyncThunk(
  "settings/getPickupPhotoRequired",
  async () => {
    return triplePSDatasource.getPickupPhotoRequired();
  }
);

export const updatePickupPhotoRequired = createAsyncThunk(
  "settings/updatePickupPhotoRequired",
  async (value: boolean, thunkAPI) => {
    const updated = await triplePSDatasource.updatePickupPhotoRequired(value);
    // Refetch so the section reflects server truth after the upsert.
    await thunkAPI.dispatch(getPickupPhotoRequired());
    return updated;
  }
);

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getServiceTime.pending, (state) => {
        state.getServiceTime = loadingAPIResult();
      })
      .addCase(getServiceTime.fulfilled, (state, action) => {
        state.getServiceTime = successAPIResult(action.payload);
        state.serviceTime = action.payload;
      })
      .addCase(getServiceTime.rejected, (state, action) => {
        state.getServiceTime = errorAPIResult(action.error);
      })
      .addCase(updateServiceTime.pending, (state) => {
        state.updateServiceTime = loadingAPIResult();
      })
      .addCase(updateServiceTime.fulfilled, (state, action) => {
        state.updateServiceTime = successAPIResult(action.payload);
      })
      .addCase(updateServiceTime.rejected, (state, action) => {
        state.updateServiceTime = errorAPIResult(action.error);
      })
      .addCase(getDumpWeight.pending, (state) => {
        state.getDumpWeight = loadingAPIResult();
      })
      .addCase(getDumpWeight.fulfilled, (state, action) => {
        state.getDumpWeight = successAPIResult(action.payload);
        state.dumpWeight = action.payload;
      })
      .addCase(getDumpWeight.rejected, (state, action) => {
        state.getDumpWeight = errorAPIResult(action.error);
      })
      .addCase(updateDumpWeight.pending, (state) => {
        state.updateDumpWeight = loadingAPIResult();
      })
      .addCase(updateDumpWeight.fulfilled, (state, action) => {
        state.updateDumpWeight = successAPIResult(action.payload);
      })
      .addCase(updateDumpWeight.rejected, (state, action) => {
        state.updateDumpWeight = errorAPIResult(action.error);
      })
      .addCase(getDailyWorkTimeCap.pending, (state) => {
        state.getDailyWorkTimeCap = loadingAPIResult();
      })
      .addCase(getDailyWorkTimeCap.fulfilled, (state, action) => {
        state.getDailyWorkTimeCap = successAPIResult(action.payload);
        state.dailyWorkTimeCap = action.payload;
      })
      .addCase(getDailyWorkTimeCap.rejected, (state, action) => {
        state.getDailyWorkTimeCap = errorAPIResult(action.error);
      })
      .addCase(updateDailyWorkTimeCap.pending, (state) => {
        state.updateDailyWorkTimeCap = loadingAPIResult();
      })
      .addCase(updateDailyWorkTimeCap.fulfilled, (state, action) => {
        state.updateDailyWorkTimeCap = successAPIResult(action.payload);
      })
      .addCase(updateDailyWorkTimeCap.rejected, (state, action) => {
        state.updateDailyWorkTimeCap = errorAPIResult(action.error);
      })
      .addCase(getDeliveryPhotoRequired.pending, (state) => {
        state.getDeliveryPhotoRequired = loadingAPIResult();
      })
      .addCase(getDeliveryPhotoRequired.fulfilled, (state, action) => {
        state.getDeliveryPhotoRequired = successAPIResult(action.payload);
        state.deliveryPhotoRequired = action.payload;
      })
      .addCase(getDeliveryPhotoRequired.rejected, (state, action) => {
        state.getDeliveryPhotoRequired = errorAPIResult(action.error);
      })
      .addCase(updateDeliveryPhotoRequired.pending, (state) => {
        state.updateDeliveryPhotoRequired = loadingAPIResult();
      })
      .addCase(updateDeliveryPhotoRequired.fulfilled, (state, action) => {
        state.updateDeliveryPhotoRequired = successAPIResult(action.payload);
      })
      .addCase(updateDeliveryPhotoRequired.rejected, (state, action) => {
        state.updateDeliveryPhotoRequired = errorAPIResult(action.error);
      })
      .addCase(getDumpReceiptRequired.pending, (state) => {
        state.getDumpReceiptRequired = loadingAPIResult();
      })
      .addCase(getDumpReceiptRequired.fulfilled, (state, action) => {
        state.getDumpReceiptRequired = successAPIResult(action.payload);
        state.dumpReceiptRequired = action.payload;
      })
      .addCase(getDumpReceiptRequired.rejected, (state, action) => {
        state.getDumpReceiptRequired = errorAPIResult(action.error);
      })
      .addCase(updateDumpReceiptRequired.pending, (state) => {
        state.updateDumpReceiptRequired = loadingAPIResult();
      })
      .addCase(updateDumpReceiptRequired.fulfilled, (state, action) => {
        state.updateDumpReceiptRequired = successAPIResult(action.payload);
      })
      .addCase(updateDumpReceiptRequired.rejected, (state, action) => {
        state.updateDumpReceiptRequired = errorAPIResult(action.error);
      })
      .addCase(getPickupPhotoRequired.pending, (state) => {
        state.getPickupPhotoRequired = loadingAPIResult();
      })
      .addCase(getPickupPhotoRequired.fulfilled, (state, action) => {
        state.getPickupPhotoRequired = successAPIResult(action.payload);
        state.pickupPhotoRequired = action.payload;
      })
      .addCase(getPickupPhotoRequired.rejected, (state, action) => {
        state.getPickupPhotoRequired = errorAPIResult(action.error);
      })
      .addCase(updatePickupPhotoRequired.pending, (state) => {
        state.updatePickupPhotoRequired = loadingAPIResult();
      })
      .addCase(updatePickupPhotoRequired.fulfilled, (state, action) => {
        state.updatePickupPhotoRequired = successAPIResult(action.payload);
      })
      .addCase(updatePickupPhotoRequired.rejected, (state, action) => {
        state.updatePickupPhotoRequired = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getServiceTime = initialState.getServiceTime;
        state.updateServiceTime = initialState.updateServiceTime;
        state.serviceTime = undefined;
        state.getDumpWeight = initialState.getDumpWeight;
        state.updateDumpWeight = initialState.updateDumpWeight;
        state.dumpWeight = undefined;
        state.getDailyWorkTimeCap = initialState.getDailyWorkTimeCap;
        state.updateDailyWorkTimeCap = initialState.updateDailyWorkTimeCap;
        state.dailyWorkTimeCap = undefined;
        state.getDeliveryPhotoRequired = initialState.getDeliveryPhotoRequired;
        state.updateDeliveryPhotoRequired = initialState.updateDeliveryPhotoRequired;
        state.deliveryPhotoRequired = undefined;
        state.getDumpReceiptRequired = initialState.getDumpReceiptRequired;
        state.updateDumpReceiptRequired = initialState.updateDumpReceiptRequired;
        state.dumpReceiptRequired = undefined;
        state.getPickupPhotoRequired = initialState.getPickupPhotoRequired;
        state.updatePickupPhotoRequired = initialState.updatePickupPhotoRequired;
        state.pickupPhotoRequired = undefined;
      });
  },
});

export default settingsSlice.reducer;
